import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import SummaryApi from '../../../common/index';

const CreateJobModal = ({ onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    title: '',
    department: 'Software Development',
    description: '',
    responsibilities: [''],
    requirements: [''],
    requiredSkills: [''],
    preferredSkills: [''],
    experienceLevel: 'Junior',
    experienceYears: { min: 0, max: 5 },
    qualifications: [''],
    salaryRange: { min: 0, max: 0 },
    jobType: 'Full-Time',
    workMode: 'On-Site',
    location: 'Remote',
    openings: 1,
    perksAndBenefits: [''],
    domain: 'SaaS',
    priority: 'Medium',
    applicationDeadline: '',
    isActive: true
  });

  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('basic');
  const [errors, setErrors] = useState({});
  const [previewMode, setPreviewMode] = useState(false);
  const [autoSaveStatus, setAutoSaveStatus] = useState('');
  const [draggedItem, setDraggedItem] = useState(null);

  // Auto-save functionality
  useEffect(() => {
    const timer = setTimeout(() => {
      if (formData.title) {
        localStorage.setItem('jobDraft', JSON.stringify(formData));
        setAutoSaveStatus('Draft saved');
        setTimeout(() => setAutoSaveStatus(''), 2000);
      }
    }, 3000);
    
    return () => clearTimeout(timer);
  }, [formData]);

  // Load draft on mount
  useEffect(() => {
    const draft = localStorage.getItem('jobDraft');
    if (draft) {
      try {
        const parsedDraft = JSON.parse(draft);
        setFormData(parsedDraft);
        setAutoSaveStatus('Draft restored');
        setTimeout(() => setAutoSaveStatus(''), 2000);
      } catch (e) {
        console.error('Failed to parse draft', e);
      }
    }
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors({...errors, [name]: ''});
    }
    
    if (type === 'checkbox') {
      setFormData({ ...formData, [name]: checked });
    } else if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData({
        ...formData,
        [parent]: {
          ...formData[parent],
          [child]: value
        }
      });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleArrayChange = (index, field, value) => {
    const updatedArray = [...formData[field]];
    updatedArray[index] = value;
    setFormData({ ...formData, [field]: updatedArray });
  };

  const addArrayItem = (field) => {
    setFormData({ ...formData, [field]: [...formData[field], ''] });
  };

  const removeArrayItem = (index, field) => {
    const updatedArray = [...formData[field]];
    updatedArray.splice(index, 1);
    setFormData({ ...formData, [field]: updatedArray });
  };

  // Drag and drop for reordering array items
  const handleDragStart = (e, field, index) => {
    setDraggedItem({ field, index });
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (e, field, dropIndex) => {
    e.preventDefault();
    if (!draggedItem || draggedItem.field !== field) return;
    
    const draggedIndex = draggedItem.index;
    if (draggedIndex === dropIndex) return;
    
    const updatedArray = [...formData[field]];
    const [removed] = updatedArray.splice(draggedIndex, 1);
    updatedArray.splice(dropIndex, 0, removed);
    
    setFormData({ ...formData, [field]: updatedArray });
    setDraggedItem(null);
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.title.trim()) {
      newErrors.title = 'Job title is required';
    }
    
    if (!formData.description.trim()) {
      newErrors.description = 'Job description is required';
    }
    
    if (formData.experienceYears.min > formData.experienceYears.max) {
      newErrors.experienceYears = 'Minimum years cannot be greater than maximum years';
    }
    
    if (formData.salaryRange.min > formData.salaryRange.max) {
      newErrors.salaryRange = 'Minimum salary cannot be greater than maximum salary';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Please fix the errors in the form');
      return;
    }
    
    setLoading(true);

    try {
      const response = await fetch(SummaryApi.createJobOpening.url, {
        method: SummaryApi.createJobOpening.method,
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (response.ok) {
        toast.success('Job opening created successfully');
        localStorage.removeItem('jobDraft'); // Clear draft after successful submission
        onSuccess();
      } else {
        toast.error(data.message || 'Failed to create job opening');
      }
    } catch (error) {
      toast.error('Error creating job opening');
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'basic', label: 'Basic Info', icon: '📝' },
    { id: 'details', label: 'Details', icon: '📋' },
    { id: 'requirements', label: 'Requirements', icon: '✅' },
    { id: 'benefits', label: 'Benefits', icon: '🎁' },
    { id: 'settings', label: 'Settings', icon: '⚙️' }
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'basic':
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Job Title *</label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.title ? 'border-red-500' : 'border-gray-300'}`}
                  placeholder="e.g. Senior Frontend Developer"
                />
                {errors.title && <p className="mt-1 text-sm text-red-600">{errors.title}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Department *</label>
                <select
                  name="department"
                  value={formData.department}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="Software Development">Software Development</option>
                  <option value="Frontend Engineering">Frontend Engineering</option>
                  <option value="Backend Engineering">Backend Engineering</option>
                  <option value="Full Stack Development">Full Stack Development</option>
                  <option value="DevOps">DevOps</option>
                  <option value="QA / Testing">QA / Testing</option>
                  <option value="UI/UX Design">UI/UX Design</option>
                  <option value="Product Management">Product Management</option>
                  <option value="Data Science / AI">Data Science / AI</option>
                  <option value="Technical Support">Technical Support</option>
                  <option value="HR / Operations">HR / Operations</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Job Description *</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows="5"
                  className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.description ? 'border-red-500' : 'border-gray-300'}`}
                  placeholder="Provide a detailed description of the role, company culture, and what makes this position exciting..."
                ></textarea>
                {errors.description && <p className="mt-1 text-sm text-red-600">{errors.description}</p>}
              </div>
            </div>
          </div>
        );
      
      case 'details':
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Experience Level</label>
                <select
                  name="experienceLevel"
                  value={formData.experienceLevel}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="Fresher">Fresher</option>
                  <option value="Junior">Junior</option>
                  <option value="Mid-Level">Mid-Level</option>
                  <option value="Senior">Senior</option>
                  <option value="Lead">Lead</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Experience Years</label>
                <div className="flex items-center space-x-2">
                  <input
                    type="number"
                    name="experienceYears.min"
                    value={formData.experienceYears.min}
                    onChange={handleChange}
                    min="0"
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <span className="text-gray-500">to</span>
                  <input
                    type="number"
                    name="experienceYears.max"
                    value={formData.experienceYears.max}
                    onChange={handleChange}
                    min="0"
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                {errors.experienceYears && <p className="mt-1 text-sm text-red-600">{errors.experienceYears}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Job Type</label>
                <select
                  name="jobType"
                  value={formData.jobType}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="Full-Time">Full-Time</option>
                  <option value="Part-Time">Part-Time</option>
                  <option value="Contract">Contract</option>
                  <option value="Internship">Internship</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Work Mode</label>
                <select
                  name="workMode"
                  value={formData.workMode}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="On-Site">On-Site</option>
                  <option value="Remote">Remote</option>
                  <option value="Hybrid">Hybrid</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
                <input
                  type="text"
                  name="location"
                  value={formData.location}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g. San Francisco, CA or Remote"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Number of Openings</label>
                <input
                  type="number"
                  name="openings"
                  value={formData.openings}
                  onChange={handleChange}
                  min="1"
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Application Deadline</label>
                <input
                  type="date"
                  name="applicationDeadline"
                  value={formData.applicationDeadline}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Salary Range</label>
                <div className="flex items-center space-x-2">
                  <input
                    type="number"
                    name="salaryRange.min"
                    value={formData.salaryRange.min}
                    onChange={handleChange}
                    min="0"
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <span className="text-gray-500">to</span>
                  <input
                    type="number"
                    name="salaryRange.max"
                    value={formData.salaryRange.max}
                    onChange={handleChange}
                    min="0"
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                {errors.salaryRange && <p className="mt-1 text-sm text-red-600">{errors.salaryRange}</p>}
              </div>
            </div>
          </div>
        );
      
      case 'requirements':
        return (
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Responsibilities</label>
              <div className="bg-gray-50 p-4 rounded-md">
                {formData.responsibilities.map((responsibility, index) => (
                  <div 
                    key={index} 
                    className="flex items-center mb-3 bg-white p-3 rounded-md shadow-sm"
                    draggable
                    onDragStart={(e) => handleDragStart(e, 'responsibilities', index)}
                    onDragOver={handleDragOver}
                    onDrop={(e) => handleDrop(e, 'responsibilities', index)}
                  >
                    <div className="mr-2 cursor-move text-gray-400">
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z"></path>
                      </svg>
                    </div>
                    <input
                      type="text"
                      value={responsibility}
                      onChange={(e) => handleArrayChange(index, 'responsibilities', e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Describe a key responsibility"
                    />
                    {formData.responsibilities.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeArrayItem(index, 'responsibilities')}
                        className="ml-2 text-red-500 hover:text-red-700"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                        </svg>
                      </button>
                    )}
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => addArrayItem('responsibilities')}
                  className="mt-2 px-4 py-2 bg-blue-50 text-blue-600 rounded-md hover:bg-blue-100 transition-colors font-medium text-sm"
                >
                  + Add Responsibility
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Requirements</label>
              <div className="bg-gray-50 p-4 rounded-md">
                {formData.requirements.map((requirement, index) => (
                  <div 
                    key={index} 
                    className="flex items-center mb-3 bg-white p-3 rounded-md shadow-sm"
                    draggable
                    onDragStart={(e) => handleDragStart(e, 'requirements', index)}
                    onDragOver={handleDragOver}
                    onDrop={(e) => handleDrop(e, 'requirements', index)}
                  >
                    <div className="mr-2 cursor-move text-gray-400">
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z"></path>
                      </svg>
                    </div>
                    <input
                      type="text"
                      value={requirement}
                      onChange={(e) => handleArrayChange(index, 'requirements', e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Describe a requirement"
                    />
                    {formData.requirements.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeArrayItem(index, 'requirements')}
                        className="ml-2 text-red-500 hover:text-red-700"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                        </svg>
                      </button>
                    )}
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => addArrayItem('requirements')}
                  className="mt-2 px-4 py-2 bg-blue-50 text-blue-600 rounded-md hover:bg-blue-100 transition-colors font-medium text-sm"
                >
                  + Add Requirement
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Required Skills</label>
                <div className="bg-gray-50 p-4 rounded-md">
                  {formData.requiredSkills.map((skill, index) => (
                    <div key={index} className="flex items-center mb-3">
                      <input
                        type="text"
                        value={skill}
                        onChange={(e) => handleArrayChange(index, 'requiredSkills', e.target.value)}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="e.g. React, Node.js"
                      />
                      {formData.requiredSkills.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeArrayItem(index, 'requiredSkills')}
                          className="ml-2 text-red-500 hover:text-red-700"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                          </svg>
                        </button>
                      )}
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => addArrayItem('requiredSkills')}
                    className="mt-2 px-4 py-2 bg-blue-50 text-blue-600 rounded-md hover:bg-blue-100 transition-colors font-medium text-sm"
                  >
                    + Add Skill
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Preferred Skills</label>
                <div className="bg-gray-50 p-4 rounded-md">
                  {formData.preferredSkills.map((skill, index) => (
                    <div key={index} className="flex items-center mb-3">
                      <input
                        type="text"
                        value={skill}
                        onChange={(e) => handleArrayChange(index, 'preferredSkills', e.target.value)}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="e.g. TypeScript, GraphQL"
                      />
                      {formData.preferredSkills.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeArrayItem(index, 'preferredSkills')}
                          className="ml-2 text-red-500 hover:text-red-700"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                          </svg>
                        </button>
                      )}
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => addArrayItem('preferredSkills')}
                    className="mt-2 px-4 py-2 bg-blue-50 text-blue-600 rounded-md hover:bg-blue-100 transition-colors font-medium text-sm"
                  >
                    + Add Skill
                  </button>
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Qualifications</label>
              <div className="bg-gray-50 p-4 rounded-md">
                {formData.qualifications.map((qualification, index) => (
                  <div 
                    key={index} 
                    className="flex items-center mb-3 bg-white p-3 rounded-md shadow-sm"
                    draggable
                    onDragStart={(e) => handleDragStart(e, 'qualifications', index)}
                    onDragOver={handleDragOver}
                    onDrop={(e) => handleDrop(e, 'qualifications', index)}
                  >
                    <div className="mr-2 cursor-move text-gray-400">
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z"></path>
                      </svg>
                    </div>
                    <input
                      type="text"
                      value={qualification}
                      onChange={(e) => handleArrayChange(index, 'qualifications', e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g. Bachelor's degree in Computer Science"
                    />
                    {formData.qualifications.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeArrayItem(index, 'qualifications')}
                        className="ml-2 text-red-500 hover:text-red-700"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                        </svg>
                      </button>
                    )}
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => addArrayItem('qualifications')}
                  className="mt-2 px-4 py-2 bg-blue-50 text-blue-600 rounded-md hover:bg-blue-100 transition-colors font-medium text-sm"
                >
                  + Add Qualification
                </button>
              </div>
            </div>
          </div>
        );
      
      case 'benefits':
        return (
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Perks and Benefits</label>
              <div className="bg-gray-50 p-4 rounded-md">
                {formData.perksAndBenefits.map((perk, index) => (
                  <div 
                    key={index} 
                    className="flex items-center mb-3 bg-white p-3 rounded-md shadow-sm"
                    draggable
                    onDragStart={(e) => handleDragStart(e, 'perksAndBenefits', index)}
                    onDragOver={handleDragOver}
                    onDrop={(e) => handleDrop(e, 'perksAndBenefits', index)}
                  >
                    <div className="mr-2 cursor-move text-gray-400">
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z"></path>
                      </svg>
                    </div>
                    <input
                      type="text"
                      value={perk}
                      onChange={(e) => handleArrayChange(index, 'perksAndBenefits', e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g. Health insurance, 401(k) matching"
                    />
                    {formData.perksAndBenefits.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeArrayItem(index, 'perksAndBenefits')}
                        className="ml-2 text-red-500 hover:text-red-700"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                        </svg>
                      </button>
                    )}
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => addArrayItem('perksAndBenefits')}
                  className="mt-2 px-4 py-2 bg-blue-50 text-blue-600 rounded-md hover:bg-blue-100 transition-colors font-medium text-sm"
                >
                  + Add Perk/Benefit
                </button>
              </div>
            </div>
          </div>
        );
      
      case 'settings':
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Domain</label>
                <select
                  name="domain"
                  value={formData.domain}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="EdTech">EdTech</option>
                  <option value="FinTech">FinTech</option>
                  <option value="HealthTech">HealthTech</option>
                  <option value="E-commerce">E-commerce</option>
                  <option value="Gaming">Gaming</option>
                  <option value="SaaS">SaaS</option>
                  <option value="AI/ML">AI/ML</option>
                  <option value="Blockchain">Blockchain</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                <select
                  name="priority"
                  value={formData.priority}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="Low">Low</option>
                  <option value="Medium">Medium</option>
                  <option value="High">High</option>
                  <option value="Urgent">Urgent</option>
                </select>
              </div>

              <div className="col-span-2">
                <div className="bg-gray-50 p-4 rounded-md">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      name="isActive"
                      id="isActive"
                      checked={formData.isActive}
                      onChange={handleChange}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="isActive" className="ml-2 block text-sm text-gray-700">
                      Active (show on Careers page)
                    </label>
                  </div>
                  <p className="mt-2 text-sm text-gray-500">
                    When enabled, this job opening will be visible on the careers page and accepting applications.
                  </p>
                </div>
              </div>
            </div>
          </div>
        );
      
      default:
        return null;
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-screen overflow-y-auto my-8">
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
          <h2 className="text-2xl font-bold text-gray-800">Create New Job Opening</h2>
          <div className="flex items-center space-x-2">
            {autoSaveStatus && (
              <span className="text-sm text-green-600 mr-2">{autoSaveStatus}</span>
            )}
            <button
              type="button"
              onClick={() => setPreviewMode(!previewMode)}
              className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
            >
              {previewMode ? 'Edit' : 'Preview'}
            </button>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          </div>
        </div>

        {previewMode ? (
          <div className="p-6">
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-xl font-bold text-gray-800 mb-4">{formData.title || 'Job Title'}</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div>
                  <span className="text-sm text-gray-500">Department:</span>
                  <p className="font-medium">{formData.department}</p>
                </div>
                <div>
                  <span className="text-sm text-gray-500">Experience Level:</span>
                  <p className="font-medium">{formData.experienceLevel}</p>
                </div>
                <div>
                  <span className="text-sm text-gray-500">Job Type:</span>
                  <p className="font-medium">{formData.jobType}</p>
                </div>
                <div>
                  <span className="text-sm text-gray-500">Work Mode:</span>
                  <p className="font-medium">{formData.workMode}</p>
                </div>
                <div>
                  <span className="text-sm text-gray-500">Location:</span>
                  <p className="font-medium">{formData.location}</p>
                </div>
                <div>
                  <span className="text-sm text-gray-500">Openings:</span>
                  <p className="font-medium">{formData.openings}</p>
                </div>
              </div>
              
              <div className="mb-6">
                <h4 className="font-semibold text-gray-700 mb-2">Description</h4>
                <p className="text-gray-600 whitespace-pre-wrap">{formData.description || 'No description provided'}</p>
              </div>
              
              {formData.responsibilities.some(r => r.trim()) && (
                <div className="mb-6">
                  <h4 className="font-semibold text-gray-700 mb-2">Responsibilities</h4>
                  <ul className="list-disc pl-5 space-y-1">
                    {formData.responsibilities.filter(r => r.trim()).map((responsibility, index) => (
                      <li key={index} className="text-gray-600">{responsibility}</li>
                    ))}
                  </ul>
                </div>
              )}
              
              {formData.requirements.some(r => r.trim()) && (
                <div className="mb-6">
                  <h4 className="font-semibold text-gray-700 mb-2">Requirements</h4>
                  <ul className="list-disc pl-5 space-y-1">
                    {formData.requirements.filter(r => r.trim()).map((requirement, index) => (
                      <li key={index} className="text-gray-600">{requirement}</li>
                    ))}
                  </ul>
                </div>
              )}
              
              {formData.requiredSkills.some(s => s.trim()) && (
                <div className="mb-6">
                  <h4 className="font-semibold text-gray-700 mb-2">Required Skills</h4>
                  <div className="flex flex-wrap gap-2">
                    {formData.requiredSkills.filter(s => s.trim()).map((skill, index) => (
                      <span key={index} className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">{skill}</span>
                    ))}
                  </div>
                </div>
              )}
              
              {formData.perksAndBenefits.some(p => p.trim()) && (
                <div className="mb-6">
                  <h4 className="font-semibold text-gray-700 mb-2">Perks and Benefits</h4>
                  <ul className="list-disc pl-5 space-y-1">
                    {formData.perksAndBenefits.filter(p => p.trim()).map((perk, index) => (
                      <li key={index} className="text-gray-600">{perk}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        ) : (
          <>
            <div className="border-b">
              <div className="flex">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setActiveTab(tab.id)}
                    className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                      activeTab === tab.id
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    <span className="mr-2">{tab.icon}</span>
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>

            <form onSubmit={handleSubmit} className="p-6">
              {renderTabContent()}

              <div className="mt-8 flex justify-between">
                <div className="text-sm text-gray-500">
                  <p>* Required fields</p>
                  <p className="mt-1">Your work is automatically saved as a draft</p>
                </div>
                <div className="flex space-x-3">
                  <button
                    type="button"
                    onClick={onClose}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="px-6 py-2 bg-blue-600 border border-transparent rounded-md font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 flex items-center"
                  >
                    {loading ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Creating...
                      </>
                    ) : (
                      'Create Job Opening'
                    )}
                  </button>
                </div>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default CreateJobModal;